import React from 'react';
import LongText from '@components/long-text/long-text';
import vars from '@variables';
import { GatsbyImage } from 'gatsby-plugin-image';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H1Atom } from '@atoms/text-atoms/text-atoms';
import {
  GatsbyImageDataWithFile,
  HomepageMastheadProps,
} from './homepage-masthead.types';
import { LetterboxLinkAtom } from '@atoms/link-atoms/link-atoms';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HomepageMastheadContainer } from './homepage-masthead.styles';
import { useBreakpointValue } from '@hooks/use-breakpoint-value';

const getRandomImage = (images: GatsbyImageDataWithFile[]) => {
  return images[Math.floor(Math.random() * images.length)];
};

const getFileType = (url: string) => {
  const contentType = url.split('.').pop();
  return contentType === 'gif' ? 'image/gif' : contentType;
};

const RandomizedBackgroundImage: React.FC<{
  mobileImages: GatsbyImageDataWithFile[];
  tabletImages: GatsbyImageDataWithFile[];
  desktopImages: GatsbyImageDataWithFile[];
  imageAlt: string;
}> = ({ mobileImages, tabletImages, desktopImages, imageAlt }) => {
  const [isClient, setIsClient] = React.useState(false);
  const [randomImages, setRandomImages] = React.useState<{
    desktop: GatsbyImageDataWithFile;
    tablet: GatsbyImageDataWithFile;
    mobile: GatsbyImageDataWithFile;
  } | null>(null);

  React.useEffect(() => {
    setIsClient(true);
    setRandomImages({
      desktop: getRandomImage(desktopImages),
      tablet: getRandomImage(tabletImages),
      mobile: getRandomImage(mobileImages),
    });
  }, []);

  const currentImage = useBreakpointValue(
    [
      {
        breakpoint: vars.breakPointFullWidth,
        value: randomImages?.desktop,
      },
      {
        breakpoint: vars.breakPointTablet,
        value: randomImages?.tablet,
      },
      {
        breakpoint: vars.breakPointMinWidth,
        value: randomImages?.mobile,
      },
    ],
    randomImages?.desktop
  );

  if (!isClient) return null;

  if (!currentImage) return null;

  const isGif =
    getFileType(currentImage.file?.contentType || '') === 'image/gif';

  return (
    <div className="background-image">
      {!isGif ? (
        <GatsbyImage
          image={currentImage}
          alt={imageAlt}
          className="gatsby-image-pos art-directed"
          loading="eager"
        />
      ) : (
        currentImage.file?.url && (
          <img
            src={currentImage.file.url}
            alt={imageAlt}
            className="gatsby-image-pos masthead-gif"
          />
        )
      )}
    </div>
  );
};

const HomepageMasthead: React.FC<HomepageMastheadProps> = ({ masthead }) => {
  const {
    title,
    description,
    enableLightText,
    cta,
    backgroundColour,
    mobileImages,
    tabletImages,
    desktopImages,
  } = masthead;

  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.03], [0.65, 0]);

  return (
    <HomepageMastheadContainer
      data-testid="homepage-masthead"
      backgroundColour={backgroundColour}
      cta={cta}
      enableLightText={enableLightText}
    >
      <div className="content-frame">
        <div className="text-content">
          <H1Atom>{title}</H1Atom>
          {description && <LongText content={description} />}
          {cta && (
            <LetterboxLinkAtom
              url={cta.url}
              gtmId={GTM_ID.HOMEPAGE_MASTHEAD_CTA}
              dataTestId="homepage banner cta button"
            >
              {cta.text}
            </LetterboxLinkAtom>
          )}
        </div>
      </div>
      <motion.div className="icon-scroll" style={{ opacity }} />
      <RandomizedBackgroundImage
        mobileImages={mobileImages}
        tabletImages={tabletImages}
        desktopImages={desktopImages}
        imageAlt={description || ''}
      />
    </HomepageMastheadContainer>
  );
};

export default HomepageMasthead;
